/**
 * Which of the current client's funding sources were shared TO them by their partner. Those render
 * read-only everywhere: only the partner who shared an account can change or unlink it.
 *
 * Canonical rule: an own source with `partnerAction: 'mirrored'` whose `addedByClientId` is someone
 * else. Shares made before that field existed are only recorded in the partner's guided-planning
 * session; a host that reads that legacy data passes it in as `legacy` (guided planning
 * does, the D2C flow reads no guided session). Also loads the partner client for names and totals.
 */
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';

import { selectClient, selectMaybeClientPartner } from '@client/model';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { clientsApi, clientKeys } from '@client/api/waterlily';

export interface LegacyPartnerSelection {
  action: string;
  partnerFundingSourceId?: string;
  originalValue: number;
  customClientPercent?: number;
}

export interface LegacyPartnerSelections {
  selections: Record<string, LegacyPartnerSelection> | undefined;
  isLoading: boolean;
}

export function usePartnerLockedFundingSources(legacy?: LegacyPartnerSelections) {
  const client = useSelector(selectClient);
  // The partner record that arrives with the client. Consumer sessions don't carry the
  // `partnerFundingSplit` flag, so the fetch below never runs for them and this supplies the name.
  const loadedPartner = useSelector(selectMaybeClientPartner);
  const { flags } = useFeatureFlags();
  const featureEnabled = flags.partnerFundingSplit === true;

  const hasPartnerClientId = featureEnabled && !!client?.partnerClientId;

  const { data: partnerClient = null, isLoading: isLoadingPartnerClient } = useQuery({
    queryKey: clientKeys.detail(client?.partnerClientId ?? ''),
    queryFn: async () => {
      const response = (await clientsApi.getById(
        client!.partnerClientId!,
      )) as unknown as ClientContainer;
      return response.client ?? null;
    },
    enabled: hasPartnerClientId,
    staleTime: 5 * 60 * 1000,
  });

  const isLoading = hasPartnerClientId && (isLoadingPartnerClient || !!legacy?.isLoading);

  const {
    partnerSharedFundingSourceIds,
    partnerSharedActionByFundingSourceId,
    partnerSharedOriginalValueByFundingSourceId,
    partnerSharedCustomPercentByFundingSourceId,
  } = useMemo(() => {
    const ids = new Set<string>();
    const actionMap: Record<string, string> = {};
    const originalValueMap: Record<string, number> = {};
    const customPercentMap: Record<string, number | undefined> = {};

    // Primary: derive from the current client's own sources via addedByClientId.
    // Works for splits initiated by either the modal or guided planning, and is
    // independent of any session metadata.
    const currentClientId = client?.clientId ?? null;
    const mySelfFunding = client?.multipleFundingSources?.selfFunding ?? {};
    Object.entries(mySelfFunding).forEach(([id, source]) => {
      if (source.partnerAction !== 'mirrored') return;
      if (!source.addedByClientId) return;
      if (currentClientId != null && source.addedByClientId === currentClientId) return;
      ids.add(id);
      const splitRatio = source.splitRatio ?? null;
      actionMap[id] =
        splitRatio == null ? 'mirror' : splitRatio === 50 ? 'split-50-50' : 'split-custom';
      originalValueMap[id] = source.existingAssetsValue ?? 0;
      customPercentMap[id] = splitRatio != null && splitRatio !== 50 ? splitRatio : undefined;
    });

    // Fallback: legacy splits without `addedByClientId`, from the partner's guided-session
    // metadata when the host supplies it.
    const entries = Object.values(legacy?.selections ?? {});
    for (const s of entries) {
      if (!s.partnerFundingSourceId || ids.has(s.partnerFundingSourceId)) continue;
      ids.add(s.partnerFundingSourceId);
      actionMap[s.partnerFundingSourceId] = s.action;
      originalValueMap[s.partnerFundingSourceId] = s.originalValue;
      customPercentMap[s.partnerFundingSourceId] = s.customClientPercent;
    }

    return {
      partnerSharedFundingSourceIds: ids,
      partnerSharedActionByFundingSourceId: actionMap,
      partnerSharedOriginalValueByFundingSourceId: originalValueMap,
      partnerSharedCustomPercentByFundingSourceId: customPercentMap,
    };
  }, [client?.clientId, client?.multipleFundingSources?.selfFunding, legacy?.selections]);

  return {
    isLoading,
    partnerClient,
    partnerSharedFundingSourceIds,
    partnerSharedActionByFundingSourceId,
    partnerSharedOriginalValueByFundingSourceId,
    partnerSharedCustomPercentByFundingSourceId,
    partnerFirstName:
      partnerClient?.clientFirstName ??
      (loadedPartner && loadedPartner.clientId === client?.partnerClientId
        ? loadedPartner.clientFirstName
        : null) ??
      null,
  };
}
